// Best-effort mapping from a 6-digit Indian postal pincode to the 2-digit GST
// state code, using the standard postal-circle prefix ranges. This is a direct
// port of the server's com.bedrock.utils.PincodeStateMapper — keep the two in
// step so the outpost's e-Way bill prediction matches what the server does on sync.
//
// Postal prefixes do not always align with state borders (a few 3-digit prefixes
// straddle two states), so ambiguous prefixes deliberately return 0 (unknown)
// rather than guessing — callers must fall back to another source (e.g. the GSTIN
// state). Pure/stateless — no dependencies, no I/O.

/**
 * @param {string|number} pincode - a 6-digit Indian postal pincode (stray
 *        separators like "560 001" are tolerated).
 * @returns {number} the 2-digit GST state code, or 0 when the pincode is
 *          invalid, unassigned, or its prefix is ambiguous between two states.
 *
 * Examples:
 *   stateCodeForPincode("560001") -> 29  (Bengaluru → Karnataka)
 *   stateCodeForPincode("682001") -> 32  (Kochi → Kerala)
 *   stateCodeForPincode("247667") -> 0   (Haridwar/Roorkee is ambiguous by design)
 */
export function stateCodeForPincode(pincode) {
    // Tolerate stray separators ("560 001"); anything but exactly 6 digits
    // (not starting with 0) is unknown.
    const digits = pincode == null ? "" : String(pincode).replace(/[^0-9]/g, "");
    if (!/^[1-9][0-9]{5}$/.test(digits)) {
        return 0;
    }
    const first3 = parseInt(digits.substring(0, 3), 10);
    const first2 = parseInt(digits.substring(0, 2), 10);

    // 3-digit exceptions first (union territories / states carved out of a 2-digit range).
    if (first3 === 160) return 4;    // Chandigarh (inside the 16x Punjab range)
    if (first3 === 194) return 38;   // Ladakh (Leh/Kargil, inside the 19x J&K range)
    if (first3 >= 246 && first3 <= 249) return 0; // ambiguous: Uttarakhand / western UP share these
    if (first3 === 262 || first3 === 263) return 0; // ambiguous: Uttarakhand (Nainital) / UP (Pilibhit).
    // NOTE: Haridwar 247xxx returns 0 by design (Uttarakhand/UP overlap).
    if (first3 === 403) return 30;   // Goa (inside the 40-44 Maharashtra range)
    if (first3 === 605) return 34;   // Puducherry (inside the 60-64 Tamil Nadu range)
    if (first3 === 737) return 11;   // Sikkim (inside the 70-74 West Bengal range)
    if (first3 === 744) return 35;   // Andaman & Nicobar Islands (inside the 70-74 WB range)
    if (first3 >= 790 && first3 <= 792) return 12; // Arunachal Pradesh
    if (first3 === 793 || first3 === 794) return 17; // Meghalaya
    if (first3 === 795) return 14;   // Manipur
    if (first3 === 796) return 15;   // Mizoram
    if (first3 === 797 || first3 === 798) return 13; // Nagaland
    if (first3 === 799) return 16;   // Tripura
    // Bihar (10) / Jharkhand (20) share the 80-85 band; these 3-digit prefixes are
    // confidently Jharkhand (Santhal Parganas, Palamu, Hazaribagh, Dhanbad, Bokaro,
    // Ramgarh, Singhbhum, Ranchi). 813, 823, 824 stay Bihar via the 2-digit rule below.
    if ((first3 >= 814 && first3 <= 816) || first3 === 822 || (first3 >= 825 && first3 <= 835)) {
        return 20; // Jharkhand
    }

    // 2-digit postal circles.
    switch (first2) {
        case 11:
            return 7;   // Delhi
        case 12:
        case 13:
            return 6;   // Haryana
        case 14:
        case 15:
        case 16:
            return 3;   // Punjab (160 Chandigarh handled above)
        case 17:
            return 2;   // Himachal Pradesh
        case 18:
        case 19:
            return 1;   // Jammu & Kashmir (194 Ladakh handled above)
        case 20:
        case 21:
        case 22:
        case 23:
        case 24:
        case 25:
        case 26:
        case 27:
        case 28:
            return 9;   // Uttar Pradesh (Uttarakhand-shared prefixes handled above)
        case 30:
        case 31:
        case 32:
        case 33:
        case 34:
            return 8;   // Rajasthan
        case 36:
        case 37:
        case 38:
        case 39:
            return 24;  // Gujarat
        case 40:
        case 41:
        case 42:
        case 43:
        case 44:
            return 27;  // Maharashtra (403 Goa handled above)
        case 45:
        case 46:
        case 47:
        case 48:
            return 23;  // Madhya Pradesh
        case 49:
            return 22;  // Chhattisgarh
        case 50:
            return 36;  // Telangana
        case 51:
        case 52:
        case 53:
            return 37;  // Andhra Pradesh
        case 56:
        case 57:
        case 58:
        case 59:
            return 29;  // Karnataka
        case 60:
        case 61:
        case 62:
        case 63:
        case 64:
            return 33;  // Tamil Nadu (605 Puducherry handled above)
        case 67:
        case 68:
        case 69:
            return 32;  // Kerala (682 Lakshadweep intentionally kept as Kerala)
        case 70:
        case 71:
        case 72:
        case 73:
        case 74:
            return 19;  // West Bengal (737 Sikkim, 744 Andaman handled above)
        case 75:
        case 76:
        case 77:
            return 21;  // Odisha
        case 78:
            return 18;  // Assam
        case 79:
            return 0;   // North-East prefixes not caught above are unassigned
        case 80:
        case 81:
        case 82:
        case 83:
        case 84:
        case 85:
            return 10;  // Bihar (Jharkhand prefixes handled above)
        default:
            return 0;   // unassigned / unknown
    }
}
